import {derived, get, writable} from 'svelte/store';
import {whiteLabeling} from 'Store/WhiteLabelStore';
import type {
  Deal,
  Investor,
  InvestorResponse,
  Setting,
} from 'Utility/investorFlow/entity';
import {checkoutStateIsVerificationOrAfter} from 'Utility/investorFlow/checkoutState';
import {InvestorState} from 'Utility/investorFlow/investorState';
import {apiGetDeal} from 'Utility/investorFlow/investorFlowAPI';
import {ApiTarget} from 'Types/api';
import api from 'Utility/api';
import type {Section} from 'Store/ibex/checkouts/sectionsStore';
import {
  sectionsStore,
  SectionsId,
  InvestorCheckoutState,
} from 'Store/ibex/checkouts/sectionsStore';
import {entity, selectedProfileId} from './profileStore';
import {stepByEntityType} from 'Utility/checkoutFlow/contactInformation/selectEntity';
import {steps} from 'Store/investorProfile/currentStepStore';
import {getCurrentUserId} from 'Utility/document';
import {apiUrl} from 'Store/InvestorFlowStore';
import {DealKind as DealRaiseTypeCodes} from 'Types/deals/deals';
import {
  State,
  VerificationMethod,
} from 'Types/deals/investors/verification506cRequests';
import type {DealFeatureFlags} from 'Types/deals/dealFeatureFlags';

export {DealRaiseTypeCodes};

export type AdditionalDocument = {
  id: number;
  filename: string;
  description: string;
  url: string;
};

export type DealType = {
  isRegA: boolean;
  isRegCf: boolean;
  isRegD506c: boolean;
  isRegS: boolean;
  isOm: boolean;
};

export const deal = writable<Deal>(undefined);
export const investor = writable<Investor>(undefined);
export const generateAgreement = writable<boolean>(false);
export const documentUploadInProgress = writable<boolean>(false);

export const dealFlags = derived(
  deal,
  ($deal): DealFeatureFlags => $deal?.feature_flags || {}
);

export const dealSettings = derived(
  deal,
  ($deal): Partial<Setting> => $deal?.setting || {}
);

const isRaiseType = (deal: Deal, code: DealRaiseTypeCodes): boolean =>
  deal?.raise_type_code === code;

export const dealType = derived(deal, ($deal): DealType => {
  return {
    isRegA: isRaiseType($deal, DealRaiseTypeCodes.REG_A),
    isRegCf: isRaiseType($deal, DealRaiseTypeCodes.REG_CF),
    isRegD506c: isRaiseType($deal, DealRaiseTypeCodes.REG_D_506_C),
    isRegS: isRaiseType($deal, DealRaiseTypeCodes.REG_S),
    isOm: isRaiseType($deal, DealRaiseTypeCodes.OM),
  };
});

// Disable certain action on investor for any one other than the investor themselves.
export const notOwner = derived(investor, $investor => {
  return getCurrentUserId() !== $investor?.user?.id;
});

export const legalLimitApplied = writable<boolean>(false);

export const fetchDeal = async (dealId: string | number): Promise<void> => {
  try {
    const response = await apiGetDeal(get(apiUrl), String(dealId));
    deal.set(response);
    whiteLabeling.set(response.white_labeling);
  } catch (error) {
    deal.set(undefined);
  }
};

export const fetchInvestor = async (
  investorId: string | number
): Promise<void> => {
  try {
    const currentDeal = get(deal);
    const whitelistedDomain = document.location.href;

    const payload: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        'X-DEALMAKER-DOMAIN': whitelistedDomain,
      },
    };

    const body = await api.getJson<Investor>(
      ApiTarget.EmbedV1,
      `deals/${currentDeal.id}/investors/${investorId}`,
      payload
    );

    investor.set(body);
  } catch (error) {
    investor.set(undefined);
  }
};

export const updateInvestmentAmount = async (payload: {
  number_of_securities?: number;
  investment_amount?: number;
}): Promise<void> => {
  try {
    const currentDeal = get(deal);
    const currentInvestor = get(investor);
    const whitelistedDomain = document.location.href;

    const init: RequestInit = {
      headers: {
        'X-DEALMAKER-DOMAIN': whitelistedDomain,
      },
    };

    const body = await api.patchJson<InvestorResponse>(
      ApiTarget.V1,
      `deals/${currentDeal.id}/investors/${currentInvestor.id}/investment_amount`,
      payload,
      init
    );

    investor.update(investor => {
      return {
        ...investor,
        investment_amount: body.investment_value,
        number_of_securities: body.number_of_securities,
        checkout_state: body.checkout_state,
        is_funded: body.is_funded,
        enough_total_funds_plus_pending_to_be_funded:
          body.enough_total_funds_plus_pending_to_be_funded,
      };
    });
  } catch (error) {
    investor.update(investor => {
      return {...investor, investment_amount: 0};
    });
  }
};

export const onCloseState = (section: Section): void => {
  sectionsStore.updateSection(section, {finished: true});
  sectionsStore.closeAllSections();

  const nextSection = sectionsStore
    .nextSections(section)
    .find(section => !section.finished);
  if (nextSection) {
    sectionsStore.updateSection(nextSection, {opened: true});
  }
};

export const openSectionGivenInvestorCheckoutState = (): void => {
  sectionsStore.closeAllSections();

  const investorCheckoutState = get(investor)?.checkout_state;

  openSectionOnFirstLoad(investorCheckoutState);
};

export const onChangeState = (section: Section) => {
  sectionsStore.updateSection(section, {opened: true, finished: false});
};

const FINAL_SECTION_IDS = [SectionsId?.STAGE_TERMS_CONDITIONS];

const sectionMatchesProhibitedState = (section: Section): boolean => {
  const match = section.id.match(
    new RegExp(`\\b${FINAL_SECTION_IDS.join('|')}\\b$`)
  );
  return match !== null;
};

const finalSectionsAreFinished = (): boolean => {
  let finished = false;

  FINAL_SECTION_IDS.forEach(sectionId => {
    const section = sectionsStore.findSectionById(sectionId);

    if (section?.finished) finished = true;
  });

  return finished;
};

const termsConditionsFinished = (): boolean => {
  return sectionsStore.findSectionById(SectionsId.STAGE_TERMS_CONDITIONS)
    ?.finished;
};

const paymentsFinished = (): boolean => {
  return (
    get(investor)?.is_funded &&
    sectionsStore.findSectionById(SectionsId.STAGE_PAYMENT)?.finished
  );
};

const blockOpeningInvestmentAmountSection = (section: Section): boolean => {
  const investorValue = get(investor);

  // can't reopen if investor has an allocated amount set
  const allocatedAmount = investorValue.allocated_amount;
  if (section.id === SectionsId.STAGE_INVESTMENT_AMOUNT && allocatedAmount)
    return true;

  // can't reopen investment amount section if investor has signed, funded or has enough funds to be funded.
  // But does not show the message about locked investment amount.
  const signedOrFundedInvestor =
    investorValue.state === InvestorState.SIGNED ||
    investorValue.is_funded ||
    investorValue.enough_total_funds_plus_pending_to_be_funded;
  if (
    section.id === SectionsId.STAGE_INVESTMENT_AMOUNT &&
    signedOrFundedInvestor
  )
    return true;

  return false;
};

const stateDocumentUpload = (): boolean =>
  get(investor).checkout_state === InvestorCheckoutState.DOCUMENT_UPLOAD;

export const allPreviousSectionsFinished = (section: Section): boolean => {
  return sectionsStore.previousSections(section).every(v => v.finished);
};

export const canOpenSection = (section: Section): boolean => {
  const allSections = sectionsStore.getAllSections();

  // Case where the every section is finished
  if (allSections.every(v => v.finished)) return false;

  const isProhibitedSection = sectionMatchesProhibitedState(section);
  const previousSectionsFinished = allPreviousSectionsFinished(section);

  // Case where the user tries to open Payment or Terms and Conditions but haven't finished previous ones
  if (isProhibitedSection && !previousSectionsFinished) return false;

  // Case where the user tries to open first steps but have already finished a final section
  if (!isProhibitedSection && finalSectionsAreFinished()) return false;

  // Case where the previous section is not finished
  if (!previousSectionsFinished) return false;

  if (blockOpeningInvestmentAmountSection(section)) return false;

  if (stateDocumentUpload()) return false;

  if (
    section.id === SectionsId.STAGE_TERMS_CONDITIONS &&
    termsConditionsFinished()
  )
    return false;
  // can't reopen payments when the investor is funded
  if (section.id === SectionsId.STAGE_PAYMENT && paymentsFinished())
    return false;

  return true;
};

// A basic way to not allow users to open the next section when the previous is not finished
export const openSection = (section: Section): void => {
  if (!canOpenSection(section)) return;

  sectionsStore.closeAllSections();
  sectionsStore.updateSection(section, {opened: !section.opened});
};

export const openSectionOnFirstLoad = (stage: string): void => {
  if (stage === InvestorCheckoutState.PRE_CHECKOUT) {
    stage = SectionsId.STAGE_INVESTMENT_AMOUNT;
  }

  if (redirectToVerificationsSection(stage)) {
    stage = SectionsId.STAGE_VERIFICATIONS;
  }

  const currentSection = sectionsStore.findSectionById(stage);
  let allPreviousSections = null;

  if (currentSection) {
    allPreviousSections = sectionsStore.previousSections(currentSection);
  }

  sectionsStore.update(sections => {
    return sections.map(section => {
      if (
        allPreviousSections?.find((prev: Section) => prev.id === section.id)
      ) {
        section.finished = true;
      }

      if (section.id === stage) {
        section.opened = true;
      }

      if (
        section.id === InvestorCheckoutState.CONTACT_INFORMATION &&
        get(selectedProfileId)
      ) {
        // Always allowing to edit profile on clicking the closed detail section
        const step = stepByEntityType(get(entity).type);
        steps.set([step]);
      }

      return section;
    });
  });

  if (stage === InvestorCheckoutState.CHECKOUT_COMPLETE) {
    sectionsStore.update(sections => {
      return sections.map(section => {
        return {...section, opened: false, finished: true};
      });
    });
  }
};

export const isExternalVerifier = (verificationMethod: String) =>
  verificationMethod === VerificationMethod.EXTERNAL_VERIFIER;

const pendingExternalReview = (investor: Investor) => {
  return (
    isExternalVerifier(
      investor?.verification_506c_request?.verification_method
    ) &&
    !investor?.verification_506c_request?.external_accreditation_verifier
      ?.request_submitted
  );
};

export const newDocumentsRequested = (investor: Investor) => {
  // There is an edde case for external verifier method, the verification state can be new documents requested
  // and verification be pending on the external verifier instead of the investor if
  // the investor submitted the email, the external verifier submits the form by saying the investor cannot be marked as accredited,
  // and the investor submits the email again.
  // This use case does not change the verification state to pending, it leaves it as new documents requested
  return (
    investor?.verification_506c_request?.state ===
      State.NEW_DOCUMENTS_REQUESTED && !pendingExternalReview(investor)
  );
};

const redirectToVerificationsSection = (stage: string) => {
  if (!get(dealFlags).enable_506c_improvements) {
    return false;
  }

  const verification506cRequest = get(investor)?.verification_506c_request;
  const skippedVerification =
    verification506cRequest === null &&
    checkoutStateIsVerificationOrAfter(stage as InvestorCheckoutState);

  if (
    stage === InvestorCheckoutState.INVESTOR_CONFIRMATION ||
    newDocumentsRequested(get(investor)) ||
    skippedVerification
  ) {
    return true;
  }

  return false;
};

export const completePaymentStage = async () => {
  const currentDeal = get(deal);
  const currentInvestor = get(investor);

  const body = await api.patchJson<Investor>(
    ApiTarget.Internal,
    `deals/${currentDeal.id}/investors/${currentInvestor.id}/complete_payment_stage`
  );

  investor.update(investor => {
    return {...investor, checkout_state: body.checkout_state};
  });
};

export const signWhenClickThroughEligible = async (): Promise<
  void | object
> => {
  const dealId = get(deal)?.id;
  const investorId = get(investor)?.id;

  const response = await api.patch(
    ApiTarget.Internal,
    `deals/${dealId}/investors/${investorId}/clickthrough/sign`
  );

  if (!response.ok) {
    const responseData = await response.json();
    throw new Error(responseData ? responseData.error : 'Server Error');
  }
};

export const triggerPreviewAgreementEvent = async () => {
  const dealId = get(deal)?.id;
  const investorId = get(investor)?.id;

  if (!dealId || !investorId) return;

  try {
    await api.patchJson(
      ApiTarget.Internal,
      `deals/${dealId}/investors/${investorId}/trigger_preview_agreement_event`
    );
  } catch (error) {
    console.error(error);
  }
};

export const resubmitAgreement = async (): Promise<void | object> => {
  const dealId = get(deal)?.id;
  const investorId = get(investor)?.id;

  const response = await api.patch(
    ApiTarget.Internal,
    `deals/${dealId}/investors/${investorId}/clickthrough/resubmit`
  );

  if (!response.ok) {
    const responseData = await response.json();
    throw new Error(responseData ? responseData.error : 'Server Error');
  }
};
